import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money, STUDIO } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { fmtDate } from "../lib/format";
import BillingSchedule from "./BillingSchedule";
import { ScopeSentinel } from "./ScopeSentinel";
import { ExpensesPanel } from "./ExpensesPanel";
import { ShareStanding } from "../components/ShareStanding";


import type { Project, Variation } from "../lib/types";

/* Billing has its own room rather than hiding behind a toggle inside
   Quotations — the payment structure is where an existing project starts. */
export default function Billing() {
  const p = useCurrent();
  const canEdit = useCan("payments");
  const session = useSession();
  if (!p) return null;
  return (
    <Page>
      <PageHead kicker={`${p.name} · Finance`} title="Billing & payment schedule">
        {canEdit && <ShareStanding p={p} by={session?.name ?? STUDIO.principal} />}
      </PageHead>
      <BillingSchedule p={p} canEdit={canEdit} />
      <ExpensesPanel p={p} canEdit={canEdit} by={session?.name ?? STUDIO.principal} />
      <CertificatesPanel p={p} canEdit={canEdit} by={session?.name ?? STUDIO.principal} />
      <VariationsPanel p={p} canEdit={canEdit} />
      <ScopeSentinel p={p} canEdit={canEdit} by={session?.name ?? STUDIO.principal} />
    </Page>
  );
}

/* Payment certificates under the construction contract, mirrored from the
   Contract Studio. Each carries the live certificate link and the contractor's
   invoice; "Send to client" emails both parties the document of record (the
   outcome of each send lands on the project's records). */
function CertificatesPanel({ p, canEdit, by }: { p: Project; canEdit: boolean; by: string }) {
  const certs = [...(p.certificates ?? [])].sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? "")));
  const cur = certs[0]?.currency ?? p.currency;
  const total = certs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const recordFor = (id: string) => (p.records ?? []).filter((r) => r.kind === "certificate-issued" && r.refId === id).sort((a, b) => b.on.localeCompare(a.on))[0];
  return (
    <div className="card reveal" style={{ overflow: "hidden", marginTop: 22 }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <div className="row" style={{ gap: 8 }}><Icon.money width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Payment certificates — construction contract</span></div>
        <span className="row" style={{ gap: 10 }}>
          {certs.length > 0 && <span className="dim" style={{ fontSize: 12 }}>{certs.length} issued · {money(total, cur)}</span>}
          <a className="btn ghost sm" href={`https://contracts.studiogy.design/?go=payments`} target="_blank" rel="noreferrer">Open Contract Studio</a>
        </span>
      </div>
      {certs.length === 0 ? (
        <div className="dim" style={{ padding: "22px 18px", fontSize: 13 }}>No certificates yet. Issue them in the Contract Studio's payments ledger — link that ledger to this project and each certificate appears here with its invoice, ready to send to the client.</div>
      ) : (
        <div className="col" style={{ gap: 0 }}>
          {certs.map((ct, i) => {
            const rec = recordFor(ct.id);
            const sent = rec?.recipients?.filter((r) => r.result === "delivered").map((r) => r.to) ?? [];
            const pending = rec?.recipients?.some((r) => r.result === "pending");
            return (
              <div key={ct.id} style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
                <div className="row between wrap" style={{ gap: 10 }}>
                  <span className="row wrap" style={{ gap: 10, minWidth: 0, alignItems: "baseline" }}>
                    <span className="badge accent mono" style={{ fontSize: 10.5 }}>{ct.number}</span>
                    <span style={{ fontSize: 14.5, fontWeight: 600 }}>{ct.title}</span>
                    <span className="dim" style={{ fontSize: 12 }}>{ct.contractName} · {ct.payee}</span>
                  </span>
                  <span className="mono" style={{ fontSize: 14.5, fontWeight: 600 }}>{money(ct.amount, ct.currency)}</span>
                </div>
                {ct.description && <div className="dim" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>{ct.description}</div>}
                <div className="row wrap" style={{ gap: 8, marginTop: 10, alignItems: "center" }}>
                  {ct.shareUrl && <a className="btn ghost sm" href={ct.shareUrl} target="_blank" rel="noreferrer">Certificate</a>}
                  {ct.invoice?.url ? <a className="btn ghost sm" href={ct.invoice.url} target="_blank" rel="noreferrer">Invoice · {ct.invoice.name}</a> : <span className="dim" style={{ fontSize: 11.5 }}>no invoice attached yet</span>}
                  {canEdit && (
                    <button className="btn accent sm" disabled={pending} onClick={() => actions.record(p.id, { kind: "certificate-issued", refId: ct.id, title: `${ct.title} ${ct.number}`, by })}>
                      <Icon.send width={13} />{rec ? "Send to client again" : "Send to client"}
                    </button>
                  )}
                  <span className="dim" style={{ fontSize: 11.5 }}>
                    {ct.date}
                    {rec ? ` · ${pending ? "sending…" : sent.length ? `sent ${fmtDate(rec.on)} to ${sent.join(", ")}` : "send failed — check the client's email"}` : ""}
                    {(ct.emails?.length ?? 0) > 0 ? ` · emailed ${ct.emails!.length}× from the Contract Studio` : ""}
                    {ct.receiptConfirmedBy ? ` · receipt confirmed by ${ct.receiptConfirmedBy}` : ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const VSTATUS: Record<string, { label: string; hue: string }> = {
  draft: { label: "Draft", hue: "var(--ink-3)" }, issued: { label: "Awaiting approval", hue: "var(--warn)" },
  approved: { label: "Approved", hue: "var(--ok)" }, rejected: { label: "Rejected", hue: "var(--danger)" },
};

function VariationsPanel({ p, canEdit }: { p: Project; canEdit: boolean }) {
  const session = useSession();
  const who = session?.name ?? STUDIO.principal;
  const [raise, setRaise] = useState(false);
  const cur = p.billing?.currency ?? p.currency;
  const vars = [...(p.variations ?? [])].sort((a, b) => b.ref.localeCompare(a.ref, undefined, { numeric: true }));
  const approvedTotal = vars.filter((v) => v.status === "approved").reduce((s, v) => s + v.amount, 0);

  return (
    <div className="card reveal" style={{ overflow: "hidden", marginTop: 22 }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <div className="row" style={{ gap: 8 }}><Icon.swap width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Variations / change orders</span></div>
        <span className="row" style={{ gap: 10 }}>
          {approvedTotal > 0 && <span className="dim" style={{ fontSize: 12 }}>{money(approvedTotal, cur)} approved</span>}
          {canEdit && <button className="btn accent sm" onClick={() => setRaise(true)}><Icon.spark width={13} />Raise variation</button>}
        </span>
      </div>
      {vars.length === 0 ? (
        <div className="dim" style={{ padding: "22px 18px", fontSize: 13 }}>No variations. Raise one when a scope change adds to the fee — the client approves it and it bills as an invoice.</div>
      ) : (
        <div className="col" style={{ gap: 0 }}>
          {vars.map((v, i) => (
            <div key={v.id} style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
              <div className="row between wrap" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 10, minWidth: 0, alignItems: "baseline" }}>
                  <span className="badge accent mono" style={{ fontSize: 10.5 }}>{v.ref}</span>
                  <span style={{ fontSize: 14.5, fontWeight: 600 }}>{v.title}</span>
                </span>
                <span className="row" style={{ gap: 10 }}>
                  <span className="mono" style={{ fontSize: 13.5, fontWeight: 600 }}>{money(v.amount, cur)}</span>
                  <span className="badge" style={{ fontSize: 9, color: VSTATUS[v.status].hue }}>{VSTATUS[v.status].label}</span>
                </span>
              </div>
              <div style={{ fontSize: 13, marginTop: 5, lineHeight: 1.5 }}>{v.description}</div>
              {v.reason && <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>Reason: {v.reason}</div>}
              {v.decidedBy && <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>{v.status === "approved" ? "Approved" : "Rejected"} by {v.decidedBy}{v.decidedOn ? ` · ${fmtDate(v.decidedOn)}` : ""}{v.decisionNote ? ` — ${v.decisionNote}` : ""}</div>}
              {canEdit && (
                <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
                  {v.status === "draft" && <button className="btn accent sm" onClick={() => actions.issueVariation(p.id, v.id, who)}><Icon.send width={13} />Issue for approval</button>}
                  {v.status === "issued" && <span className="dim" style={{ fontSize: 12 }}>Sent to the client for approval.</span>}
                  {v.status === "approved" && (v.invoiceId ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Billed</span> : <button className="btn accent sm" onClick={() => actions.billVariation(p.id, v.id, who)}><Icon.money width={13} />Bill as invoice</button>)}
                  <span style={{ flex: 1 }} />
                  {v.status !== "approved" && <button className="iconbtn" style={{ width: 30, height: 30 }} title="Remove" onClick={() => { if (confirm(`Remove ${v.ref}?`)) actions.removeVariation(p.id, v.id); }}><Icon.close width={13} /></button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {raise && <RaiseVariationModal p={p} by={who} cur={cur} onClose={() => setRaise(false)} />}
    </div>
  );
}

function RaiseVariationModal({ p, by, cur, onClose }: { p: Project; by: string; cur: string; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  return (
    <Modal open onClose={onClose} title="Raise a variation">
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>An extra fee for a change beyond the agreed scope. Issue it to the client for approval, then bill it once approved.</p>
        <div className="field"><label>Title</label><input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Redesign of the entrance canopy" /></div>
        <div className="field"><label>What it covers</label><textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the additional work." /></div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Reason <span className="dim">(optional)</span></label><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Client-requested change, site condition…" /></div>
          <div className="field" style={{ width: 160 }}><label>Fee ({cur})</label><input className="input mono" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" /></div>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!title.trim() || !description.trim() || !(Number(amount) > 0)} onClick={() => { actions.createVariation(p.id, { title, description, reason, amount: Number(amount), by }); onClose(); }}><Icon.check width={15} />Save draft</button>
        </div>
      </div>
    </Modal>
  );
}

export type { Variation };
